import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Card, 
  Table, 
  Button, 
  Space, 
  Tag, 
  Tooltip, 
  message, 
  Modal,
  Input, 
  Select, 
  Row,
  Col,
  Avatar,
  Skeleton,
  Form
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  CheckCircleOutlined, 
  StopOutlined,
  SearchOutlined,
  ClockCircleOutlined,
  CheckOutlined
} from '@ant-design/icons';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import moment from 'moment';

import api from '../../services/api';
import customerService from '../../services/customerService';
import { getLicense, bindMacAddress } from '../../services/licenseService';

// Status constants
const MAC_REQUEST_STATUS = {
  PENDING: 'PENDING',
  COMPLETED: 'COMPLETED',
  DELETED: 'DELETED'
};

const { Search } = Input;
const { Option } = Select;



const getStatusTag = (status) => {
  const statusMap = {
    [MAC_REQUEST_STATUS.PENDING]: {
      color: 'warning',
      icon: <ClockCircleOutlined />,
      text: 'Pending'
    },
    [MAC_REQUEST_STATUS.COMPLETED]: {
      color: 'success',
      icon: <CheckCircleOutlined />,
      text: 'Completed'
    },
    [MAC_REQUEST_STATUS.DELETED]: {
      color: 'error',
      icon: <StopOutlined />,
      text: 'Deleted'
    }
  };

  const statusInfo = statusMap[status] || { color: 'default', text: status };
  
  return (
    <Tag 
      color={statusInfo.color} 
      icon={statusInfo.icon}
      style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
    >
      {statusInfo.text}
    </Tag>
  );
};

const MacAddressCell = ({ macData }) => {
  const [macAddress, setMacAddress] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchMacAddress = async () => {
      if (!macData?.license_id) {
        setError('No license ID provided');
        return;
      }

      try {
        setLoading(true);
        const license = await getLicense(macData.license_id);
        if (license?.mac_address) {
          setMacAddress(license.mac_address);
        } else {
          setError('No MAC address found for this license');
        }
      } catch (err) {
        console.error('Error fetching MAC address:', err);
        setError('Failed to load MAC address');
      } finally {
        setLoading(false);
      }
    };

    fetchMacAddress();
  }, [macData?.license_id]);

  if (loading) return <Skeleton.Input active size="small" style={{ width: 150 }} />;
  if (error) return <span style={{ color: '#ff4d4f' }}>No MAC assigned</span>;
  if (!macAddress) return <span>No MAC assigned</span>;

  return <Tag color="blue">{macAddress}</Tag>;
};


const CustomerCell = ({ customerData }) => {
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const processCustomerData = async () => {
      try {
        setLoading(true);
        
        const license = await getLicense(customerData?.license_id)
        
        if (license?.customer_licenses?.length > 0) {
          if (license.customer_licenses[0].customer_id) {
            customerService.getCustomer(license.customer_licenses[0].customer_id)
            .then(customer => {
              setCustomer(customer);
            })
            .catch(err => {
              console.error('Error fetching customer:', err);
              setError('Failed to load customer data');
            });
          }
          return;
        }

        // If no customer data is available
        setError('No customer data available');
      } catch (err) {
        console.error('Error processing customer data:', err);
        setError(err.message || 'Failed to load customer data');
      } finally {
        setLoading(false);
      }
    };

    processCustomerData();
  }, [customerData]);

  // Handle loading state
  if (loading) return <Skeleton.Input style={{ width: 150 }} active size="small" />;
  
  // Handle error state
  if (error) return <Tooltip title={error}><span style={{ color: '#ff4d4f' }}>Unassigned</span></Tooltip>;
  
  // Handle case where no customer data is available
  if (!customer) {
    return <span>Unassigned</span>;
  }

  // Extract display name
  const displayName = customer.company_name || `Customer #${customer.id}`;
  
  const logoUrl = customer.company_logo ? (
    customer.company_logo.startsWith('uploads/') ? 
      `${(process.env.REACT_APP_ROOT_URL || 'http://localhost:8000').replace('/api/v1', '')}/static/${customer.company_logo.replace('uploads/', '')}` :
      customer.company_logo.startsWith('/static/') ?
        `${(process.env.REACT_APP_ROOT_URL || 'http://localhost:8000').replace('/api/v1', '')}${customer.company_logo}` :
        customer.company_logo
  ) : null;

  return (
    <Tooltip title={`ID: ${customer.id}`}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {logoUrl && (
            <Avatar 
                src={logoUrl}
                size="small"
                style={{ 
                  backgroundColor: customer.company_logo ? 'transparent' : '#1890ff',
                  border: customer.status === 'active' ? '1px solid #19f705' : '1px solid #fc0202',
                  boxSizing: 'border-box',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              ></Avatar>
          )}
          <span>{displayName}</span>
        </div>
      </Tooltip>
  );
};




const MacRequestList = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [requestToDelete, setRequestToDelete] = useState(null);
  const [currentRequestId, setCurrentRequestId] = useState(null);
  const [showMacModal, setShowMacModal] = useState(false);
  const [macAddress, setMacAddress] = useState('');
  const [bindingMac, setBindingMac] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0
  });
  const [filters, setFilters] = useState({
    status: '',
    search: ''
  });

  // Fetch MAC requests
  const fetchMacRequests = useCallback(async () => {
    // Don't fetch if user is not authenticated
    if (!user?.id) {
      return;
    }
    
    // Create an AbortController to cancel the request if the component unmounts
    const controller = new AbortController();
    const signal = controller.signal;
    
    try {
      setLoading(true);
      setError(null);
      
      // Calculate skip from pagination
      const { page, limit } = pagination;
      const skip = (page - 1) * limit;
      
      // Build query parameters
      const params = new URLSearchParams();
      params.append('skip', skip);
      params.append('limit', limit);
      if (filters.status) params.append('status', filters.status);
      if (filters.search) params.append('search', filters.search);
      
      // Make the API call with error handling
      const response = await api.get(`/mac-requests?${params.toString()}`, { signal });
      // Handle the response
      if (response?.data) {
        // The API returns data in the format { items: [...], total: number, page: number, pages: number }
        const { items = [], total = 0 } = response.data;
        
        setRequests(items);
        setPagination(prev => ({
          ...prev,
          total,
        }));
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      // Don't set error if the request was aborted
      if (error.name === 'AbortError') {
        return;
      }
      
      console.error('Error fetching MAC requests:', error);
      setError('Failed to load MAC requests');
      
      // Handle specific error cases
      if (error.response?.status === 401) {
        navigate('/login');
      } else if (error.response?.status === 403) {
        // User doesn't have permission, but don't show error toast
        setRequests([]);
      } else {
        toast.error('Failed to load MAC requests. Please try again.');
      }
    } finally {
      setLoading(false);
    }
    
    // Cleanup function to abort the request if the component unmounts
    return () => controller.abort();
  }, [pagination.page, pagination.limit, filters.status, filters.search, user?.id, navigate]);

  // Handle table change (pagination, filters, sorter)
  const handleTableChange = (newPagination, filters, sorter) => {
    setPagination(prev => ({
      ...prev,
      page: newPagination.current,
      limit: newPagination.pageSize,
    }));
    
    // Handle status filter
    const statusFilter = filters.status && filters.status.length > 0 ? filters.status[0] : '';
    setFilters(prev => ({
      ...prev,
      status: statusFilter
    }));
  };

  // Handle status filter change
  const handleStatusChange = (value) => {
    
    setFilters({ ...filters, status: value });
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  // Handle search
  const handleSearch = (e) => {
    const value = e.target.value;
    setFilters({ ...filters, search: value });
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      status: '',
      search: '',
      dateRange: []
    });
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  // Handle delete confirmation
  const confirmDelete = (request) => {
    setRequestToDelete(request);
    setShowDeleteModal(true);
  };

  // Handle delete
  const handleDelete = async () => {
    if (!requestToDelete) return;
    
    try {
      await api.delete(`/mac-requests/${requestToDelete.id}`);
      message.success('Request deleted successfully');
      fetchMacRequests();
    } catch (error) {
      console.error('Error deleting request:', error);
      message.error('Failed to delete request');
    } finally {
      setShowDeleteModal(false);
      setRequestToDelete(null);
    }
  };


  // Handle date range change
  const handleDateRangeChange = (dates) => {
    setFilters({ ...filters, dateRange: dates });
    setPagination({ ...pagination, current: 1 });
  };

  // Handle status update
  const handleStatusUpdate = async (id, status) => {
    try {
      await api.patch(`/mac-requests/${id}/complete`, { status });
      message.success(`Request ${status.toLowerCase()} successfully`);
      fetchMacRequests();
    } catch (error) {
      console.error(`Error ${status.toLowerCase()}ing request:`, error);
      message.error(`Failed to ${status.toLowerCase()} request`);
    }
  };

  // Handle mark as complete
  const handleComplete = async (id) => {
    try {
      // Use the dedicated complete endpoint
      await api.post(`/mac-requests/${id}/complete`);
      message.success('Request marked as completed successfully');
      fetchMacRequests();
    } catch (error) {
      console.error('Error completing request:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to mark request as completed';
      message.error(errorMessage);
    }
  };

  // Handle MAC address binding
  const handleBindMac = async () => {
    if (!macAddress) {
      message.warning('Please enter a MAC address');
      return;
    }
    
    // Basic MAC address validation (simple format check)
    const macRegex = /^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$/;
    if (!macRegex.test(macAddress)) {
      message.error('Please enter a valid MAC address (format: 00:1A:2B:3C:4D:5E)');
      return;
    }

    try {
      setBindingMac(true);
      // Get the current request to access its license_id
      const currentRequest = requests.find(r => r.id === currentRequestId);
      if (!currentRequest?.license_id) {
        throw new Error('No license ID found for this request');
      }

      // First bind the MAC address to the license
      await api.post(`/licenses/${currentRequest.license_id}/bind-mac`, { 
        mac_address: macAddress 
      });
      
      // Mark the request as completed
      await api.post(`/mac-requests/${currentRequestId}/complete`);
      
      message.success('MAC address bound and request marked as completed successfully');
      setShowMacModal(false);
      setMacAddress('');
      fetchMacRequests(); // Refresh the list
    } catch (error) {
      console.error('Error binding MAC address:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to bind MAC address';
      message.error(errorMessage);
    } finally {
      setBindingMac(false);
    }
  };

  // Show MAC request modal
  const showMacRequestModal = (id) => {
    const request = requests.find(r => r.id === id);
    console.log('Request data:', request);
    const mac = request?.mac_address || '';
    console.log('Setting macAddress to:', mac);
    setCurrentRequestId(id);
    setMacAddress(mac);
    setShowMacModal(true);
  };

  // Fetch data on component mount and when dependencies change
  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    
    const loadData = async () => {
      try {
        await fetchMacRequests();
      } catch (error) {
        if (signal.aborted) {
          console.log('Request was aborted');
          return;
        }
        const errorMessage = error.response?.data?.message || error.message || 'Failed to load MAC requests';
        setError(errorMessage);
        toast.error(errorMessage);
      } finally {
        if (!signal.aborted) {
          setLoading(false);
        }
      }
    };
    
    loadData();
    
    // Cleanup function to cancel pending requests on unmount
    return () => {
      controller.abort();
    };
  }, [fetchMacRequests, user, pagination.page, pagination.limit, filters.status, filters.search]);


  // Memoize columns to prevent unnecessary re-renders
  const columns = React.useMemo(() => [
    {
      title: 'Request ID',
      dataIndex: 'id',
      key: 'id',
      width: 120,
      fixed: 'left',
      render: (text, record) => (
        <Button 
          type="link" 
          onClick={() => navigate(`/admin/mac-requests/${record.id}`)}
          style={{ padding: 0 }}
        >
          <code>#{text}</code>
        </Button>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: getStatusTag,
      filters: [
        { text: 'Pending', value: MAC_REQUEST_STATUS.PENDING },
        { text: 'Completed', value: MAC_REQUEST_STATUS.COMPLETED },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: 'Customer',
      key: 'customer',
      width: 200,
      render: (_, record) => {
        // If we have customer data in the license, use that
        if (record?.license_id) {
          console.log('License ID:', record?.license_id);
          return <CustomerCell customerData={{license_id: record?.license_id,}} />;
        }
        // Fallback if no customer data is available
        return <span>Unassigned</span>;
      },
    },
    {
      title: 'Current MAC',
      key: 'current_mac',
      width: 200,
      render: (_, record) => {
        // If we have a license ID, use the MacAddressCell component
        if (record.license_id) {
          return <MacAddressCell macData={{ license_id: record.license_id }} />;
        }
        
        // Fallback for direct MAC address in record (for backward compatibility)
        // if (record.current_mac) {
        //   return <Tag color="blue">{record.current_mac}</Tag>;
        // }
        
        return <span>No MAC assigned</span>;
      }
    },
    {
      title: 'Requested MAC',
      dataIndex: 'requested_mac',
      key: 'requested_mac',
      width: 160,
      render: (text, record) => {
        return record.mac_address ? <Tag color="green">{record.mac_address}</Tag> : 'N/A';
      }
    },
    {
      title: 'Requested At',
      dataIndex: 'request_date',
      key: 'request_date',
      width: 180,
      render: (text, record) => {
        console.log("record --- ", record.request_date);
        return record.request_date ? moment(record.request_date).format('YYYY-MM-DD') : 'N/A';
      },
      sorter: (a, b) => new Date(a.request_date) - new Date(b.request_date),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      fixed: 'right',
      render: (_, record) => (
      <Space size="middle">
        
        {record.status === 'PENDING' && (
          <Tooltip title="Bind MAC and Complete">
            <Button 
              type="text" 
              icon={<CheckOutlined style={{ color: '#389e0d' }} />} 
              onClick={() => showMacRequestModal(record.id)}
            />
          </Tooltip>
        )}
        
        {/* <Tooltip title="Delete">
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => {
              setRequestToDelete(record);
              setShowDeleteModal(true);
            }}
          />
        </Tooltip> */}
      </Space>
    )
    }
  ], [navigate, handleStatusUpdate]);              


  return (
    <div className="mac-request-list">
      <Card 
        title="MAC Requests"
        // extra={
        //   <Button 
        //     type="primary" 
        //     icon={<PlusOutlined />}
        //     onClick={() => navigate('/admin/mac-requests/new')}
        //   >
        //     New Request
        //   </Button>
        // }
      >
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Search
              placeholder="Search requests..."
              allowClear
              enterButton={<SearchOutlined style={{ padding: '8px' }} />}
              onSearch={value => handleSearch({ target: { value } })}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Select
              style={{ width: '100%' }}
              placeholder="Filter by status"
              allowClear
              onChange={handleStatusChange}
              value={filters.status || undefined}
            >
              <Option value={MAC_REQUEST_STATUS.PENDING}>
                <Space style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <ClockCircleOutlined style={{ color: '#faad14' }} />
                  Pending
                </Space>
              </Option>
              <Option value={MAC_REQUEST_STATUS.COMPLETED}>
                <Space style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <CheckCircleOutlined style={{ color: '#52c41a' }} />
                  Completed
                </Space>
              </Option>
              {/* <Option value={MAC_REQUEST_STATUS.DELETED}>
                <Space style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <StopOutlined style={{ color: '#ff4d4f' }} />
                  Deleted
                </Space>
              </Option> */}
            </Select>
          </Col>
        </Row>

        {error ? (
          <div className="error-message" style={{ padding: '16px', textAlign: 'center' }}>
            <p>{error}</p>
            <Button type="primary" onClick={fetchMacRequests} loading={loading}>
              Retry
            </Button>
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={requests}
            rowKey="id"
            loading={loading}
            pagination={{
              current: pagination.page,
              pageSize: pagination.limit,
              total: pagination.total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} requests`,
              pageSizeOptions: ['10', '25', '50', '100'],
              position: ['bottomRight']
            }}
            onChange={handleTableChange}
            scroll={{ x: 'max-content' }}
            bordered
            size="middle"
            className="custom-table"
            locale={{
              emptyText: 'No MAC requests found',
              triggerDesc: 'Sort descending',
              triggerAsc: 'Sort ascending',
              cancelSort: 'Cancel sorting',
            }}
          />
        )}
      </Card>

      {/* MAC Address Binding Modal */}
      <Modal
        title="Bind MAC Address"
        open={showMacModal}
        onOk={handleBindMac}
        onCancel={() => setShowMacModal(false)}
        confirmLoading={bindingMac}
        okText="Bind MAC & Complete"
        cancelText="Cancel"
      >
        <p>Enter the MAC address to bind to this license:</p>
        <Form
          initialValues={{ mac_address: macAddress }}
          key={currentRequestId} // Force re-render when request changes
        >
          <Form.Item
            label="MAC Address"
            name="mac_address"
            rules={[{ 
              required: true, 
              message: 'Please enter a MAC address' 
            }]}
          >
            <Input 
              placeholder="00:1A:2B:3C:4D:5E"
              onChange={(e) => setMacAddress(e.target.value)}
              style={{ width: '100%' }} 
            />
          </Form.Item>
        </Form>
        <p style={{ marginTop: '8px', color: '#888', fontSize: '12px' }}>
          Format: 00:1A:2B:3C:4D:5E
        </p>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        title="Confirm Delete"
        open={showDeleteModal}
        onOk={handleDelete}
        onCancel={() => setShowDeleteModal(false)}
        okText="Delete"
        okButtonProps={{ danger: true }}
        centered
      >
        <p>Are you sure you want to delete this request? This action cannot be undone.</p>
      </Modal>
    </div>
  );
};

export default MacRequestList;
